/**
 * ClientSheet — the shared CWR glass bottom-sheet shell (profile-tab-reskin, from
 * the shipped BookingDetailPage cancel-sheet idiom). Overlay + rising glass
 * container + grab handle, with an entrance AND exit animation. Every colour is an
 * injected theme token (:root[data-client-theme]); no hardcoded theme hexes.
 *
 * Body atoms (copy/nudge/actions/keep/danger/save/cancel/error) live here too so
 * the three consumers (edit sheet, remove-card sheet, booking cancel sheet) share
 * one source. Page-specific body content (e.g. the edit form fields) stays in the
 * consumer's own module and is composed alongside these classes.
 */

.overlay {
  position: fixed;
  inset: 0;
  z-index: 90;
  background: rgba(var(--bg-page-rgb), 0.62);
  backdrop-filter: blur(6px) saturate(120%);
  animation: ovlIn 0.3s ease backwards;
}
.overlayOut {
  animation: ovlOut 0.26s ease forwards;
}
@keyframes ovlIn {
  from {
    opacity: 0;
  }
}
@keyframes ovlOut {
  to {
    opacity: 0;
  }
}

.sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 95;
  border-radius: 32px 32px 0 0;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  box-shadow: inset 6px 6px 18px -8px rgba(var(--accent-light-rgb), 0.14), 0 -14px 40px rgba(0, 0, 0, 0.55);
  padding: 10px 22px calc(env(safe-area-inset-bottom, 0px) + 30px);
  max-height: calc(var(--client-sheet-visible-height, var(--app-viewport-height, 100dvh)) - 8px);
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  /* Single composed transform: the keyboard follow writes --kb-lift (lifts the sheet UP) and
     drag-to-dismiss writes --drag-y (pushes it DOWN), each per-frame from JS. NO transform
     transition here on purpose — the keyboard loop animates in JS and a CSS transition would
     lag its per-frame writes and reintroduce the settle pop. The sheetIn/sheetOut keyframes
     still override this transform while they run. */
  transform: translateY(calc(var(--drag-y, 0px) - var(--kb-lift, 0px)));
  animation: sheetIn 0.58s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}
.sheetOut {
  animation: sheetOut 0.26s cubic-bezier(0.4, 0, 1, 1) forwards;
}
@keyframes sheetIn {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
}
@keyframes sheetOut {
  to {
    transform: translateY(100%);
    opacity: 0.4;
  }
}
.sheet::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, rgba(var(--accent-light-rgb), 0.22) 0%, rgba(var(--accent-light-rgb), 0.03) 100%);
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}
.sheet > * {
  position: relative;
  z-index: 1;
}

.dragRegion {
  margin: -10px -22px 0;
  padding: 10px 22px 8px;
  touch-action: none;
  cursor: grab;
}
.grab {
  width: 72px;
  height: 28px;
  margin: -2px auto 8px;
  background: transparent;
  touch-action: none;
}
.grab::before {
  content: '';
  position: absolute;
  top: 8px;
  left: 50%;
  width: 56px;
  height: 5px;
  border-radius: 3px;
  background: rgba(var(--fg-page-rgb), 0.22);
  transform: translateX(-50%);
}
.grabDraggable {
  cursor: grab;
}

.kicker {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 7px;
}
.title {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 25px;
  letter-spacing: -0.01em;
  color: var(--text-primary);
}
.copy {
  margin-top: 8px;
  font-size: 13.5px;
  color: var(--text-secondary);
  line-height: 1.5;
}
.copy b {
  color: var(--text-primary);
}
.nudge {
  margin-top: 12px;
  font-size: 12.5px;
  color: var(--text-tertiary);
  line-height: 1.45;
}
.error {
  margin-top: 12px;
  font-size: 12.5px;
  color: var(--danger);
  line-height: 1.4;
}

/* Column actions (stacked full-width) — remove-card + booking cancel sheets. */
.actions {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
/* Row actions (side-by-side) — the edit sheet (Save + Cancel). */
.actionsRow {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.keep {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  border-radius: 14px;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font-size: 15.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  box-shadow: var(--glow-amber);
  border: none;
  cursor: pointer;
}
.danger {
  width: 100%;
  padding: 13px;
  border-radius: 14px;
  background: transparent;
  border: 1px solid rgba(var(--danger-rgb), 0.45);
  color: var(--danger);
  font-size: 14.5px;
  font-weight: 600;
  cursor: pointer;
}
.save {
  flex: 1;
  padding: 13px;
  border-radius: 14px;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font-size: 14.5px;
  font-weight: 700;
  text-align: center;
  border: none;
  cursor: pointer;
  box-shadow: var(--glow-amber);
}
.save:disabled {
  opacity: 0.5;
  box-shadow: none;
}
.cancel {
  padding: 13px 18px;
  border-radius: 14px;
  border: 1px solid var(--border-soft);
  background: transparent;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.keep:disabled,
.danger:disabled {
  opacity: 0.6;
}
